import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import type { TaskItem } from "@edge/shared";
import type { PersonalTaskInput } from "../../hooks/usePersonalTasks";
import { WriteConfirm } from "../shared/WriteConfirm";
import { CrmTaskEditDialog } from "../shared/CrmTaskEditDialog";
import { PersonalTaskEditDialog } from "../shared/PersonalTaskEditDialog";

interface TasksModalProps {
  crmTasks: TaskItem[];
  personalTasks: TaskItem[];
  /** Navigate to a deal — caller closes the modal. */
  onSelectDeal: (dealId: string) => void;
  /** CRM writes (Zoho-side; dialog handles confirm). */
  onCompleteCrm: (task: TaskItem) => Promise<void>;
  onRescheduleCrm: (task: TaskItem, newDate: string) => Promise<void>;
  onEditCrm: (
    taskId: string,
    fields: {
      subject?: string;
      due_date?: string | null;
      priority?: "Highest" | "High" | "Normal" | "Low" | "Lowest";
    },
  ) => Promise<void>;
  /** Personal writes (local D1; no confirm dialog needed). */
  onCompletePersonal: (task: TaskItem) => void;
  onReschedulePersonal: (id: string, dueDate: string) => void;
  onUpdatePersonal: (id: string, patch: PersonalTaskInput) => Promise<void> | void;
  onClose: () => void;
}

type ViewMode = "urgency" | "deal";

type UrgencyBucket =
  | "OVERDUE"
  | "DUE TODAY"
  | "DUE TOMORROW"
  | "THIS WEEK"
  | "NEXT WEEK"
  | "LATER";

const URGENCY_ORDER: UrgencyBucket[] = [
  "OVERDUE",
  "DUE TODAY",
  "DUE TOMORROW",
  "THIS WEEK",
  "NEXT WEEK",
  "LATER",
];

type Pending =
  | { kind: "complete-crm"; task: TaskItem }
  | { kind: "reschedule-crm"; task: TaskItem; date: string }
  | { kind: "edit-crm"; task: TaskItem }
  | { kind: "edit-personal"; task: TaskItem }
  | { kind: "reschedule-personal"; task: TaskItem; date: string }
  | null;

// ─── Date helpers ─────────────────────────────────────────────────────────

/** "YYYY-MM-DD" — today in Pacific time. */
function todayPT(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
  }).format(new Date());
}

/** Add `days` to a YYYY-MM-DD and return YYYY-MM-DD. */
function shiftDate(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Whole-day delta from todayISO to dueDate. Negative = overdue. */
function dayDiff(dueDate: string, todayISO: string): number {
  const day = 24 * 60 * 60 * 1000;
  const due = new Date(`${dueDate}T12:00:00Z`).getTime();
  const today = new Date(`${todayISO}T12:00:00Z`).getTime();
  return Math.round((due - today) / day);
}

interface CompactDueLabel {
  text: string;
  /** Drives the colour class — overdue=red, today=amber, dim=muted, normal=default. */
  tone: "overdue" | "today" | "normal" | "dim";
}

/**
 * Tighter due-label phrasing for the modal. Server's `task.dueLabel` is
 * preserved on the TaskItem and still rendered verbatim in the expanded
 * audit row, so the longer version is one click away.
 *
 * Buckets:
 *   - past:       "Nd overdue"           (red)
 *   - today:      "today"                (amber)
 *   - +1:         "tomorrow"             (normal)
 *   - +2..+13:    "in Nd"                (normal)
 *   - +14..+30:   "May 22 · in 3w"       (dim) — hybrid keeps anchor date + runway
 *   - +31..:      "May 22"               (dim)
 *   - no date:    "(no date)"            (dim)
 */
function compactDueLabel(
  dueDate: string | null,
  todayISO: string,
): CompactDueLabel {
  if (!dueDate) return { text: "(no date)", tone: "dim" };
  const diff = dayDiff(dueDate, todayISO);
  if (diff < 0) return { text: `${-diff}d overdue`, tone: "overdue" };
  if (diff === 0) return { text: "today", tone: "today" };
  if (diff === 1) return { text: "tomorrow", tone: "normal" };
  if (diff <= 13) return { text: `in ${diff}d`, tone: "normal" };
  const short = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
  }).format(new Date(`${dueDate}T12:00:00Z`));
  if (diff <= 30) {
    const weeks = Math.round(diff / 7);
    return { text: `${short} · in ${weeks}w`, tone: "dim" };
  }
  return { text: short, tone: "dim" };
}

/** "this week" = remainder of the current calendar week (Sun-Sat) after tomorrow. */
function bucketFor(dueDate: string | null, todayISO: string): UrgencyBucket {
  if (!dueDate) return "LATER";
  if (dueDate < todayISO) return "OVERDUE";
  if (dueDate === todayISO) return "DUE TODAY";
  const tomorrow = shiftDate(todayISO, 1);
  if (dueDate === tomorrow) return "DUE TOMORROW";

  // Compute end-of-this-week (Saturday) in PT.
  const today = new Date(`${todayISO}T12:00:00Z`);
  const daysToSat = 6 - today.getUTCDay(); // 0=Sun, 6=Sat
  const endOfWeek = shiftDate(todayISO, daysToSat);
  if (dueDate <= endOfWeek) return "THIS WEEK";

  const endOfNextWeek = shiftDate(endOfWeek, 7);
  if (dueDate <= endOfNextWeek) return "NEXT WEEK";

  return "LATER";
}

// ─── Sorting ──────────────────────────────────────────────────────────────

function priorityRank(p: TaskItem["priority"]): number {
  if (p === "high") return 0;
  if (p === "low") return 2;
  return 1; // normal / undefined
}

function compareTasks(a: TaskItem, b: TaskItem): number {
  const pr = priorityRank(a.priority) - priorityRank(b.priority);
  if (pr !== 0) return pr;
  const ad = a.dueDate ?? "9999-12-31";
  const bd = b.dueDate ?? "9999-12-31";
  if (ad !== bd) return ad.localeCompare(bd);
  return a.subject.localeCompare(b.subject);
}

// ─── Component ────────────────────────────────────────────────────────────

export function TasksModal({
  crmTasks,
  personalTasks,
  onSelectDeal,
  onCompleteCrm,
  onRescheduleCrm,
  onEditCrm,
  onCompletePersonal,
  onReschedulePersonal,
  onUpdatePersonal,
  onClose,
}: TasksModalProps) {
  const [view, setView] = useState<ViewMode>("urgency");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);

  /**
   * Tasks completed via this modal session — kept in the rendered list with a
   * COMPLETED badge until the modal closes, so accidental ✓ clicks remain
   * visible. Reload removes the task from the live `crmTasks`/`personalTasks`
   * props, so without this snapshot the row would just vanish on click.
   * State is local to the modal — closing and reopening wipes the map.
   */
  const [recentlyCompleted, setRecentlyCompleted] = useState<
    Map<string, TaskItem>
  >(new Map());

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Don't close the modal if a nested dialog is open — that dialog has
        // its own Escape handler that fires first.
        if (pending) return;
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, pending]);

  const todayISO = todayPT();

  // Merge: live tasks from props + any session-completed snapshots. The merge
  // keeps completed tasks in their original urgency/deal bucket, so the row
  // stays where it was when the user clicked ✓.
  const allTasks = useMemo(() => {
    const live = [...crmTasks, ...personalTasks];
    const liveIds = new Set(live.map((t) => t.id));
    const snapshots = [...recentlyCompleted.values()].filter(
      (t) => !liveIds.has(t.id),
    );
    return [...live, ...snapshots];
  }, [crmTasks, personalTasks, recentlyCompleted]);

  const grouped = useMemo(() => {
    if (view === "urgency") {
      const buckets = new Map<UrgencyBucket, TaskItem[]>();
      for (const b of URGENCY_ORDER) buckets.set(b, []);
      for (const t of allTasks) {
        const b = bucketFor(t.dueDate, todayISO);
        buckets.get(b)!.push(t);
      }
      for (const b of URGENCY_ORDER) {
        buckets.get(b)!.sort(compareTasks);
      }
      return URGENCY_ORDER.filter((b) => buckets.get(b)!.length > 0).map(
        (b) => ({ label: b, tasks: buckets.get(b)! }),
      );
    }

    // BY DEAL — CRM tasks bucketed by dealName, personal tasks → UNASSIGNED.
    const byDeal = new Map<string, TaskItem[]>();
    const unassigned: TaskItem[] = [];
    for (const t of allTasks) {
      if (t.source === "personal") {
        unassigned.push(t);
        continue;
      }
      const key = t.dealName ?? "(NO DEAL)";
      const list = byDeal.get(key) ?? [];
      list.push(t);
      byDeal.set(key, list);
    }
    const dealGroups = [...byDeal.entries()]
      .map(([label, tasks]) => ({ label, tasks: [...tasks].sort(compareTasks) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const groups = [...dealGroups];
    if (unassigned.length > 0) {
      groups.push({
        label: "UNASSIGNED",
        tasks: [...unassigned].sort(compareTasks),
      });
    }
    return groups;
  }, [allTasks, todayISO, view]);

  // Count visible tasks excluding session-completed (so the header reflects
  // what's actually open, not the visual row count).
  const totalOpen = crmTasks.length + personalTasks.length;

  const markCompletedInSession = (task: TaskItem) => {
    setRecentlyCompleted((m) => {
      const next = new Map(m);
      next.set(task.id, task);
      return next;
    });
  };

  return (
    <>
      <div
        className="write-confirm-backdrop"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div className="tasks-modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="tasks-modal-header">
            <div className="tasks-modal-title">
              <span className="tasks-modal-title-marker" />
              TASKS
              <span className="tasks-modal-count">· {totalOpen} OPEN</span>
            </div>
            <div className="tasks-modal-toolbar">
              <button
                className={clsx(
                  "tasks-toolbar-pill",
                  view === "urgency" && "active",
                )}
                onClick={() => setView("urgency")}
              >
                BY URGENCY
              </button>
              <button
                className={clsx(
                  "tasks-toolbar-pill",
                  view === "deal" && "active",
                )}
                onClick={() => setView("deal")}
              >
                BY DEAL
              </button>
            </div>
            <button
              className="email-detail-close"
              onClick={onClose}
              title="Close (Esc)"
              aria-label="Close tasks"
            >
              ✕
            </button>
          </div>

          <div className="tasks-modal-body">
            {totalOpen === 0 && recentlyCompleted.size === 0 ? (
              <div className="tasks-modal-empty">
                <div className="tasks-modal-empty-glyph">✓</div>
                <div className="tasks-modal-empty-headline">No open tasks.</div>
                <div className="tasks-modal-empty-sub">You're caught up.</div>
              </div>
            ) : (
              grouped.map((g) => (
                <div className="task-group" key={g.label}>
                  <div className="task-group-header">
                    <span className="task-group-label">{g.label}</span>
                    <span className="task-group-count">· {g.tasks.length}</span>
                  </div>
                  <div className="task-group-list">
                    {g.tasks.map((task) => {
                      const isCompletedInSession = recentlyCompleted.has(task.id);
                      return (
                        <TaskCard
                          key={task.id}
                          task={task}
                          todayISO={todayISO}
                          completedInSession={isCompletedInSession}
                          expanded={expandedId === task.id}
                          onToggleExpand={() =>
                            setExpandedId((cur) =>
                              cur === task.id ? null : task.id,
                            )
                          }
                          onSelectDeal={onSelectDeal}
                          onComplete={() => {
                            if (task.source === "crm") {
                              setPending({ kind: "complete-crm", task });
                            } else {
                              onCompletePersonal(task);
                              markCompletedInSession(task);
                            }
                          }}
                          onReschedule={() => {
                            if (task.source === "crm") {
                              setPending({
                                kind: "reschedule-crm",
                                task,
                                date: task.dueDate ?? todayISO,
                              });
                            } else {
                              setPending({
                                kind: "reschedule-personal",
                                task,
                                date: task.dueDate ?? todayISO,
                              });
                            }
                          }}
                          onEdit={() => {
                            if (task.source === "crm") {
                              setPending({ kind: "edit-crm", task });
                            } else {
                              setPending({ kind: "edit-personal", task });
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/*
        Nested dialogs render as SIBLINGS of the modal backdrop, not inside
        it. Otherwise click events on a date input or CANCEL button bubble up
        through the inner WriteConfirm backdrop and hit the outer modal's
        onClick={onClose}, dismissing the whole modal. Sibling rendering
        keeps each dialog's events scoped to its own subtree.
      */}
      {pending?.kind === "complete-crm" && (
        <WriteConfirm
          title="Mark complete in Zoho CRM"
          body={
            <>
              <div className="wc-task-subject">{pending.task.subject}</div>
              {pending.task.dealName && (
                <div className="wc-task-deal">
                  Linked deal: {pending.task.dealName}
                </div>
              )}
            </>
          }
          changes={[{ label: "Status", from: "Open", to: "Completed" }]}
          confirmLabel="COMPLETE"
          onConfirm={async () => {
            await onCompleteCrm(pending.task);
            markCompletedInSession(pending.task);
            setPending(null);
          }}
          onCancel={() => setPending(null)}
        />
      )}

      {pending?.kind === "reschedule-crm" && (
        <WriteConfirm
          title="Reschedule in Zoho CRM"
          body={
            <>
              <div className="wc-task-subject">{pending.task.subject}</div>
              {pending.task.dealName && (
                <div className="wc-task-deal">
                  Linked deal: {pending.task.dealName}
                </div>
              )}
              <label className="wc-date-label">
                New due date
                <input
                  className="wc-date-input"
                  type="date"
                  value={pending.date}
                  onChange={(e) =>
                    setPending({ ...pending, date: e.target.value })
                  }
                  autoFocus
                />
              </label>
            </>
          }
          changes={[
            {
              label: "Due date",
              from: shortDate(pending.task.dueDate),
              to: shortDate(pending.date),
            },
          ]}
          confirmLabel="RESCHEDULE"
          onConfirm={async () => {
            await onRescheduleCrm(pending.task, pending.date);
            setPending(null);
          }}
          onCancel={() => setPending(null)}
        />
      )}

      {pending?.kind === "reschedule-personal" && (
        <WriteConfirm
          title="Reschedule MY LIST task"
          body={
            <>
              <div className="wc-task-subject">{pending.task.subject}</div>
              <label className="wc-date-label">
                New due date
                <input
                  className="wc-date-input"
                  type="date"
                  value={pending.date}
                  onChange={(e) =>
                    setPending({ ...pending, date: e.target.value })
                  }
                  autoFocus
                />
              </label>
            </>
          }
          changes={[
            {
              label: "Due date",
              from: shortDate(pending.task.dueDate),
              to: shortDate(pending.date),
            },
          ]}
          confirmLabel="RESCHEDULE"
          onConfirm={async () => {
            onReschedulePersonal(pending.task.id, pending.date);
            setPending(null);
          }}
          onCancel={() => setPending(null)}
        />
      )}

      {pending?.kind === "edit-crm" && (
        <CrmTaskEditDialog
          task={pending.task}
          onSave={async (id, fields) => {
            await onEditCrm(id, fields);
            setPending(null);
          }}
          onCancel={() => setPending(null)}
        />
      )}

      {pending?.kind === "edit-personal" && (
        <PersonalTaskEditDialog
          task={pending.task}
          onSave={async (id, patch) => {
            await onUpdatePersonal(id, patch);
            setPending(null);
          }}
          onCancel={() => setPending(null)}
        />
      )}
    </>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────

interface TaskCardProps {
  task: TaskItem;
  todayISO: string;
  /** True when the user completed this task earlier in this modal session. */
  completedInSession: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onSelectDeal: (dealId: string) => void;
  onComplete: () => void;
  onReschedule: () => void;
  onEdit: () => void;
}

function TaskCard({
  task,
  todayISO,
  completedInSession,
  expanded,
  onToggleExpand,
  onSelectDeal,
  onComplete,
  onReschedule,
  onEdit,
}: TaskCardProps) {
  const due = compactDueLabel(task.dueDate, todayISO);
  const hasDeal =
    task.source === "crm" && !!task.dealId && !!task.dealName;

  const stop = (handler: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler();
  };

  return (
    <div
      className={clsx(
        "task-card",
        expanded && "expanded",
        completedInSession && "completed",
      )}
      onClick={onToggleExpand}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggleExpand();
        }
      }}
    >
      <div className="task-card-main">
        <span
          className={clsx(
            "task-card-badge",
            task.source === "crm" ? "badge-crm" : "badge-personal",
          )}
        >
          {task.source === "crm" ? "CRM" : "MY LIST"}
        </span>
        <div className="task-card-subject">{task.subject}</div>
        {hasDeal && (
          <>
            <span className="task-card-sep" aria-hidden>·</span>
            <button
              className="task-card-deal-pill"
              onClick={stop(() => onSelectDeal(task.dealId!))}
              title={`Open ${task.dealName}`}
            >
              ▸ {task.dealName}
            </button>
          </>
        )}
        {task.priority === "high" && !completedInSession && (
          <span className="task-card-prio prio-hi">HI</span>
        )}
        {task.priority === "low" && !completedInSession && (
          <span className="task-card-prio prio-lo">LO</span>
        )}
        {completedInSession ? (
          <span className="task-card-completed-badge">✓ COMPLETED</span>
        ) : (
          <div className={clsx("task-card-due", due.tone)}>{due.text}</div>
        )}
        {!completedInSession && (
          <div className="task-card-actions">
            <button
              className="task-card-action-btn"
              onClick={stop(onComplete)}
              title="Complete"
              aria-label="Complete task"
            >
              ✓
            </button>
            <button
              className="task-card-action-btn"
              onClick={stop(onReschedule)}
              title="Reschedule"
              aria-label="Reschedule task"
            >
              🗓
            </button>
            <button
              className="task-card-action-btn"
              onClick={stop(onEdit)}
              title="Edit"
              aria-label="Edit task"
            >
              ✎
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="task-card-expanded">
          {task.description ? (
            <div className="task-card-description">{task.description}</div>
          ) : (
            <div className="task-card-description task-card-empty-desc">
              (no description)
            </div>
          )}
          <div className="task-card-audit">
            {task.source === "crm" ? (
              <>
                <span>Source: Zoho CRM</span>
                <span>·</span>
                <span>Priority: {task.priority ?? "normal"}</span>
                <span>·</span>
                <span>{task.dueLabel}</span>
                {task.dealId && (
                  <>
                    <span>·</span>
                    <span>Deal id: {task.dealId.slice(-8)}</span>
                  </>
                )}
              </>
            ) : (
              <>
                <span>Source: MY LIST (local)</span>
                <span>·</span>
                <span>Priority: {task.priority ?? "normal"}</span>
                <span>·</span>
                <span>{task.dueLabel}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** "May 15" or "(no date)" — for write-confirm field summaries. */
function shortDate(iso?: string | null): string {
  if (!iso) return "(no date)";
  const d = new Date(`${iso}T12:00:00Z`);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
  }).format(d);
}
